/**
 * The help drawer — "This page" + "Ask". Fluent v9 rendition of references/ui-spec.md §2 and §5.
 *
 * "This page" is the readable guide for the current screen, including the parts behind a dialog:
 * the tour shows only what is on screen, the drawer deliberately does not, so a field inside a
 * dialog is still readable without opening it. "Ask" answers "where do I do X?" about anywhere
 * in PCT from the reviewed catalog.
 *
 * PCT deviation from the spec: the EN / LT / LV toggle sits in the header (not inside the Ask
 * tab), because with three languages readers switch from the "This page" tab as often.
 */
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Input, ToggleButton } from '@fluentui/react-components';
import {
  BookOpen20Regular,
  CursorClick20Regular,
  Dismiss20Regular,
  Lightbulb16Regular,
  Map20Regular,
  Play20Regular,
  Search16Regular,
  Target20Regular,
} from '@fluentui/react-icons';
import {
  groupStepsBySection,
  HELP_LANGUAGES,
  localizedContext,
  localizedPage,
  localizedSection,
  localizedStep,
  roleBadgeLabel,
  stepMatchesQuery,
  type HelpLanguage,
  type HelpStep,
  type PageHelp,
} from './types';
import { HELP_PANEL_ATTR, locateElement } from './dom';
import { HelpAsk } from './HelpAsk';
import type { HelpNotice, UserManualFile } from './HelpLauncher';
import { fmt, HELP_STRINGS, LANGUAGE_LABELS, type HelpStrings } from './strings';
import { HELP_TABSTER_ATTRS } from './tabster';
import { useDeferredValue } from './useDeferred';

/** How much of the panel stays on screen while it stands aside, so it never reads as closed. */
const YIELD_SLIVER = 14;

const StepCardBody = ({
  step,
  language,
  strings: s,
  contextHint,
  onLocate,
  onStartHere,
}: {
  step: HelpStep;
  language: HelpLanguage;
  strings: HelpStrings;
  /** How to reveal the control, when the step lives behind a UI action. */
  contextHint?: string;
  /** Flashes the control and reports whether it was on screen. */
  onLocate: (step: HelpStep) => boolean;
  /** Opens the tour ON this step. */
  onStartHere: (step: HelpStep) => void;
}) => {
  // Only set after a failed locate: the drawer does not probe the DOM up front, so a step is
  // never greyed out on a stale measurement taken before the user acted.
  const [missing, setMissing] = useState(false);
  const badge = roleBadgeLabel(step);
  const { title, body, translated } = localizedStep(step, language);

  return (
    <div className="pct-help-card">
      <div className="pct-help-card-main">
        <div className="pct-help-card-title">
          {title}
          {badge && <span className="pct-help-pill">{badge}</span>}
          {!translated && (
            <span className="pct-help-pill" title={s.untranslated}>
              EN
            </span>
          )}
        </div>
        <div className="pct-help-card-body">{body}</div>
        {contextHint && (
          <div className={`pct-help-card-context${missing ? ' pct-help-card-context--miss' : ''}`}>
            {contextHint}
          </div>
        )}
        {missing && !contextHint && (
          <div className="pct-help-card-context pct-help-card-context--miss">{s.notOnScreen}</div>
        )}
      </div>
      <div className="pct-help-card-actions">
        <button
          type="button"
          className="pct-help-icon-button"
          title={s.locate}
          aria-label={fmt(s.locateAria, { title })}
          onClick={() => setMissing(!onLocate(step))}
        >
          <Target20Regular />
        </button>
        <button
          type="button"
          className="pct-help-icon-button"
          title={s.tourFrom}
          aria-label={fmt(s.tourFromAria, { title })}
          onClick={() => onStartHere(step)}
        >
          <Play20Regular />
        </button>
      </div>
    </div>
  );
};

/** Memoised: typing a character must not re-render every card on a 50-step page. */
const StepCard = memo(StepCardBody);
StepCard.displayName = 'StepCard';

interface HelpDrawerProps {
  open: boolean;
  help: PageHelp;
  /** Role-filtered steps — the same list the launcher hands to the tour. */
  steps: HelpStep[];
  /** The reader's language, owned by the launcher so every surface agrees on it. */
  language: HelpLanguage;
  onLanguageChange: (language: HelpLanguage) => void;
  /** The user manual in the guide's language — downloaded from the header row; absent → no button. */
  userManual?: UserManualFile;
  /** Where the Ask tab's cross-page notices go (a toast host that survives navigation). */
  notify: (notice: HelpNotice) => void;
  onClose: () => void;
  onStartTour: (from?: HelpStep) => void;
  onInspect: () => void;
}

export const HelpDrawer = ({
  open,
  help,
  steps,
  language,
  onLanguageChange,
  userManual,
  notify,
  onClose,
  onStartTour,
  onInspect,
}: HelpDrawerProps) => {
  const s = HELP_STRINGS[language];
  const [tab, setTab] = useState<'page' | 'ask'>('page');
  const [query, setQuery] = useState('');
  // The list follows the typing rather than blocking it — late, not delayed (no request to spare).
  const deferredQuery = useDeferredValue(query);
  const matches = useMemo(
    () => steps.filter((step) => stepMatchesQuery(step, deferredQuery)),
    [steps, deferredQuery],
  );
  const groups = useMemo(() => groupStepsBySection(help.sections, matches), [help.sections, matches]);
  /** Standing aside so a control underneath can be seen — a peek, not a close. */
  const [yielding, setYielding] = useState(false);
  const page = localizedPage(help, language);
  const paperRef = useRef<HTMLDivElement>(null);

  // Another page's guide starts with an empty search.
  useEffect(() => setQuery(''), [help]);

  useEffect(() => {
    if (open) paperRef.current?.focus();
  }, [open]);

  // Stable identity: a changing one would defeat the memo on every card.
  const locate = useCallback((step: HelpStep): boolean => locateElement(step, { onYield: setYielding }), []);

  const peek = open && yielding;
  const trimmed = deferredQuery.trim();

  return (
    <>
      {open && (
        <div
          className={`pct-help-scrim${peek ? ' pct-help-scrim--peek' : ''}`}
          onClick={onClose}
          aria-hidden="true"
        />
      )}
      <div
        ref={paperRef}
        className={`pct-help-drawer${open ? ' pct-help-drawer--open' : ''}`}
        // Inline, per the spec: the peek must win over the open transform.
        style={peek ? { transform: `translateX(calc(100% - ${YIELD_SLIVER}px))` } : undefined}
        {...{ [HELP_PANEL_ATTR]: 'drawer' }}
        {...HELP_TABSTER_ATTRS}
        role="dialog"
        aria-modal={open}
        aria-hidden={!open}
        aria-label={`${s.eyebrow}: ${page.title}`}
        tabIndex={-1}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.stopPropagation();
            onClose();
          }
        }}
      >
        <div className="pct-help-head">
          <div className="pct-help-head-row">
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="pct-help-eyebrow">{s.eyebrow}</div>
              <div className="pct-help-title">{page.title}</div>
            </div>
            <div role="group" aria-label={s.askLanguage} data-help="help-language" style={{ display: 'flex', gap: 2 }}>
              {HELP_LANGUAGES.map((code) => (
                <ToggleButton
                  key={code}
                  size="small"
                  appearance="subtle"
                  checked={code === language}
                  onClick={() => onLanguageChange(code)}
                  style={{ minWidth: 34, padding: '0 4px', fontSize: 11 }}
                >
                  {LANGUAGE_LABELS[code]}
                </ToggleButton>
              ))}
            </div>
            {userManual && (
              <a
                className="pct-help-icon-button"
                href={userManual.href}
                download={userManual.fileName}
                aria-label={s.userManualDownload}
                title={s.userManualDownload}
                data-help="help-user-manual"
              >
                <BookOpen20Regular />
              </a>
            )}
            <button type="button" className="pct-help-icon-button" aria-label={s.close} title={s.close} onClick={onClose}>
              <Dismiss20Regular />
            </button>
          </div>

          <div className="pct-help-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              className="pct-help-tab"
              aria-selected={tab === 'page'}
              onClick={() => setTab('page')}
              data-help="help-tab-page"
            >
              {s.tabPage}
            </button>
            <button
              type="button"
              role="tab"
              className="pct-help-tab"
              aria-selected={tab === 'ask'}
              onClick={() => setTab('ask')}
              data-help="help-tab-ask"
            >
              {s.tabAsk}
            </button>
          </div>

          {tab === 'page' && (
            <>
              <p className="pct-help-intro">{page.intro}</p>
              <div className="pct-help-buttons">
                <Button
                  appearance="primary"
                  size="small"
                  icon={<Map20Regular />}
                  onClick={() => onStartTour()}
                  data-help="help-tour-button"
                >
                  {s.showMeAround}
                </Button>
                <Button
                  appearance="outline"
                  size="small"
                  icon={<CursorClick20Regular />}
                  onClick={onInspect}
                  data-help="help-inspect-button"
                >
                  {s.pointAtField}
                </Button>
              </div>
              <p className="pct-help-hint">{s.hint}</p>
              <Input
                size="small"
                style={{ width: '100%' }}
                contentBefore={<Search16Regular />}
                value={query}
                maxLength={60}
                aria-label={s.searchAria}
                placeholder={fmt(s.searchPlaceholder, { n: steps.length })}
                onChange={(_, d) => setQuery(d.value)}
                data-help="help-page-search"
              />
            </>
          )}
        </div>

        {tab === 'ask' ? (
          <HelpAsk
            language={language}
            // Navigating away leaves the guide describing a screen the user has left.
            onNavigated={onClose}
            onYield={setYielding}
            notify={notify}
          />
        ) : (
          <div className="pct-help-scroll">
            {trimmed.length > 0 && (
              <p className="pct-help-match">
                {matches.length === 0
                  ? s.noMatch
                  : fmt(s.matchLine, { n: matches.length, m: steps.length, q: trimmed })}
              </p>
            )}

            {groups.map((group) => (
              <section key={group.section}>
                <div className="pct-help-section">{localizedSection(help, group.section, language)}</div>
                {group.steps.map((step) => (
                  <StepCard
                    key={step.selector}
                    step={step}
                    language={language}
                    strings={s}
                    contextHint={step.context ? localizedContext(help, step.context, language) : undefined}
                    onLocate={locate}
                    onStartHere={onStartTour}
                  />
                ))}
              </section>
            ))}

            {/* Hidden while filtering: the tips answer no search. */}
            {trimmed.length === 0 && page.tips && page.tips.length > 0 && (
              <>
                <div className="pct-help-section">{s.tipsLabel}</div>
                {page.tips.map((tip) => (
                  <div className="pct-help-tip" key={tip}>
                    <Lightbulb16Regular />
                    <span>{tip}</span>
                  </div>
                ))}
              </>
            )}
          </div>
        )}
      </div>
    </>
  );
};
